/**
 * Score: what came back, and what the eight rules make of it.
 *
 * The seating table is the answer — who plays which pitch, at what mark, and how far above the
 * seat below. The rule list beneath it prints an em dash for every predicate that judged
 * nothing, because a rule that had nothing to say did not approve of anything.
 */

import { Link, Navigate } from "react-router-dom";
import { PREDICATES } from "@app/shared-types";
import { Pill, RuleBar, VerdictPill } from "../components/marks";
import Board from "../components/Board";
import { ACTION, ACTION_LIVE } from "../components/Chrome";
import { Kicker } from "../components/Page";
import { rate } from "../lib/format";
import { useBrief } from "../state/brief";

export default function ScorePage(): JSX.Element {
  const brief = useBrief();
  const result = brief.result;
  if (result === null) return <Navigate to="/write" replace />;

  const verdict = result.verdict;
  const byName = new Map(verdict?.judgements.map((one) => [one.predicate, one]) ?? []);
  const families = new Set(result.staves.map((one) => one.instrument));

  return (
    <Board
      kicker="Orchestration result"
      title={`${result.placed} of ${result.total} chords placed`}
      back={{ to: "/write", label: "The brief" }}
      lede={`${result.staves.length} seats · ${families.size} distinct instruments · ${result.regime} strength table`}
      actions={
        <>
          <Link to="/write" className={ACTION}>
            Write another
          </Link>
          <Link to="/export" className={ACTION_LIVE}>
            Export MusicXML
          </Link>
        </>
      }
      measure="wide"
    >
      <div className="space-y-8">
        <div className="flex flex-wrap items-center gap-2">
          <VerdictPill faults={verdict === null ? null : verdict.faultCount} />
          <span className="font-mono text-[28px] leading-none">{rate(verdict?.score)}</span>
          <span className="text-xs text-ink-faded">
            overall · coverage {rate(verdict?.coverage)}
          </span>
        </div>

        {result.problems.length > 0 ? (
          <ul className="space-y-1 rounded-xl border border-fault/40 bg-fault/5 p-4 text-sm text-fault-strong">
            {result.problems.map((problem, index) => (
              <li key={index}>{problem}</li>
            ))}
          </ul>
        ) : null}

        <section className="space-y-4">
          <Kicker>Seating</Kicker>
          {/* One card per chord, and a brief is often eight of them: two abreast where the
            window has room, because a seating plan is compared chord to chord. */}
          <div className="grid items-start gap-4 xl:grid-cols-2">
            {result.chords.map((chord, index) => (
              <div key={index} className="overflow-hidden rounded-xl border border-rule bg-leaf">
                <div className="flex items-baseline gap-3 border-b border-rule-faint px-4 py-3">
                  <span className="font-mono text-[11px] text-ink-quiet">{index + 1}</span>
                  <span className="font-mono text-[15px]">{brief.chords[index] ?? "—"}</span>
                  {chord.voicing === null ? (
                    <span className="ml-auto text-xs text-fault-strong">not placed</span>
                  ) : (
                    <span className="ml-auto font-mono text-xs text-ink-quiet">
                      span{" "}
                      {chord.voicing.pitches[chord.voicing.pitches.length - 1]! -
                        chord.voicing.pitches[0]!}{" "}
                      st
                    </span>
                  )}
                </div>
                {chord.seats.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-ink-faded">
                    No voicing satisfied the rules for this chord over this roster.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-quiet">
                        <th className="px-4 py-2 font-medium">Seat</th>
                        <th className="px-2 py-2 font-medium">Pitch</th>
                        <th className="px-2 py-2 font-medium">Mark</th>
                        <th className="px-4 py-2 text-right font-medium">Gap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...chord.seats].reverse().map((seat, at) => (
                        <tr key={at} className="border-t border-rule-faint">
                          <td className="px-4 py-2.5">{seat.name ?? seat.partId ?? "—"}</td>
                          <td className="px-2 py-2.5 font-mono">{seat.pitchName}</td>
                          <td className="px-2 py-2.5 font-mono text-ink-faded">
                            {seat.mark ?? "—"}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-ink-quiet">
                            {seat.gap === null ? "—" : `${seat.gap}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {chord.balance.length > 0 ? (
                  <p className="border-t border-rule-faint px-4 py-2.5 text-xs text-fault-strong">
                    {chord.balance.join(" · ")}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-3xl space-y-2">
          <div className="flex items-baseline justify-between">
            <Kicker>Eight rules</Kicker>
            <span className="font-mono text-xs text-ink-quiet">
              coverage {rate(verdict?.coverage)}
            </span>
          </div>
          <div className="rounded-xl border border-rule bg-leaf px-4">
            {PREDICATES.map((name) => {
              const judged = byName.get(name);
              return (
                <RuleBar
                  key={name}
                  name={name}
                  score={judged?.score ?? null}
                  detail={judged && judged.judged > 0 ? `${judged.judged}` : "silent"}
                />
              );
            })}
          </div>
          <p className="max-w-2xl text-xs leading-relaxed text-ink-faded">
            {(verdict?.silent.length ?? 0) > 0
              ? `${verdict?.silent.length} rule${verdict?.silent.length === 1 ? "" : "s"} judged nothing here and score nothing. Silence is not approval.`
              : `All ${PREDICATES.length} rules had something to judge here.`}
          </p>
          <Link
            to="/rules"
            className="inline-flex min-h-[44px] items-center text-sm font-medium text-clean hover:text-clean-strong"
          >
            The rules →
          </Link>
        </section>

        {result.placed < result.total ? <Pill tone="fault">partly placed</Pill> : null}
      </div>
    </Board>
  );
}
